import React, { useEffect, useContext, useState } from "react";
import Header from "../../components/Layout/Header";
import Sidebar from "../../components/Layout/Sidebar";
import Main from "../../components/Layout/Main";
import StationsContext from "../../context/otogas/stations/stationsContext";
import CustomersContext from "../../context/otogas/customers/customersContext";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import AddSaleModal from "../sales/addSale";
import SweetAlertWrapper from "../../components/Layout/sweetAlert";
import AuthContext from "../../context/auth/authContext";
import userCan from "../../config/user_can";
import UpdateTotalizersModal from "./update";
import { exportVariancesReport } from "../../api/otogas";
import { formatDate } from "../../api/util";

// Simple Bootstrap-styled pagination component
const Pagination = ({ currentPage, totalPages, setCurrentPage }) => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let page = 1; page <= totalPages; page++) {
        pages.push(
            <li
                key={page}
                className={`page-item ${page === currentPage ? "active" : ""}`}
            >
                <button
                    className="page-link"
                    onClick={() => setCurrentPage(page)}
                    disabled={page === currentPage}
                >
                    {page}
                </button>
            </li>,
        );
    }

    const goToPrev = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const goToNext = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    return (
        <nav aria-label="Shift variances pagination" className="mt-3">
            <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={goToPrev}>
                        Previous
                    </button>
                </li>
                {pages}
                <li
                    className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                >
                    <button className="page-link" onClick={goToNext}>
                        Next
                    </button>
                </li>
            </ul>
        </nav>
    );
};

const Shiftvariances = () => {
    const stationsContext = useContext(StationsContext);
    const customersContext = useContext(CustomersContext);
    const authContext = useContext(AuthContext);

    const { shift_variances = [], listShiftvariances } = stationsContext;
    const { clearVariance, notification, clear_notification } = customersContext;
    const { user } = authContext;

    const [showDeferModal, setShowDeferModal] = useState(false);
    const [showWriteOffModal, setShowWriteOffModal] = useState(false);
    const [showAddSaleModal, setShowAddSaleModal] = useState(false);
    const [showUpdateTotalizersModal, setShowUpdateTotalizersModal] =
        useState(false);
    const [selectedVariance, setSelectedVariance] = useState(null);
    const [deferReason, setDeferReason] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState({ text: "", type: "" });

    const [searchTerm, setSearchTerm] = useState("");
    const [filterStation, setFilterStation] = useState("");
    const [filterNozzle, setFilterNozzle] = useState("");
    const [download_loading, setDownloadLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        listShiftvariances();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (notification) {
            setAlertMessage({
                text: notification.text,
                type: notification.type,
            });
            setShowAlert(true);
        }
    }, [notification]);

    // Reset to first page whenever filters or search change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStation, filterNozzle]);

    const handle_downloads = async (func, setLoading) => {
        setLoading(true);
        try {
            const res = await func();
            if (res === "File not found") {
                setAlertMessage({
                    text: "No transactions found",
                    type: "info",
                });
                setShowAlert(true);
            } else if (res === "You are not authorized to download this file") {
                setAlertMessage({
                    text: "You are not authorized to download this file",
                    type: "info",
                });
                setShowAlert(true);
            } else {
                // File download handled by the API util / browser
                return res;
            }
        } catch (error) {
            console.error("Error downloading the file:", error);
            setAlertMessage({
                text: "An error occurred while downloading the file",
                type: "danger",
            });
            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    };

    // Apply search + station/nozzle filters
    const filteredVariances = (shift_variances || [])
        .filter((variance) => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            return (
                variance.stationName.toLowerCase().includes(term) ||
                variance.name.toLowerCase().includes(term) ||
                variance.shiftNumber.includes(searchTerm)
            );
        })
        .filter((variance) => {
            if (!filterStation && !filterNozzle) return true;
            return (
                (filterStation
                    ? variance.stationName === filterStation
                    : true) &&
                (filterNozzle ? variance.nozzleName === filterNozzle : true)
            );
        });

    // Pagination calculations based on filtered data
    const totalItems = filteredVariances.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

    const currentItems = filteredVariances.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
    );

    const handleDefer = (shiftNumber) => {
        setSelectedVariance(shiftNumber); // just the shift number
        setShowDeferModal(true);
    };

    const handleWriteOff = (shiftNumber) => {
        setSelectedVariance(shiftNumber); // just the shift number
        setShowWriteOffModal(true);
    };

    const handleConfirmDefer = async () => {
        if (deferReason) {
            await clearVariance("defer", selectedVariance, deferReason);
            setShowDeferModal(false);
            setDeferReason("");
        } else {
            alert("Please provide a reason to defer.");
        }
    };

    const handleConfirmWriteOff = async () => {
        await clearVariance("write_off", selectedVariance);
        setShowWriteOffModal(false);
    };

    const handleShowAddSaleModal = (variance) => {
        setSelectedVariance(variance); // full variance object for AddSale
        setShowAddSaleModal(true);
    };

    const handleClose = () => {
        setShowDeferModal(false);
        setShowWriteOffModal(false);
        setShowAddSaleModal(false);
        setShowUpdateTotalizersModal(false);
    };

    const handleConfirm = () => {
        if (notification && notification.type === "success") {
            handleClose();
        }
        listShiftvariances();
        setShowAlert(false);
        clear_notification();
    };

    const page = "Shift Variances"; // if used for breadcrumbs somewhere else

    return (
        <>
            <Header />
            <Sidebar />
            <Main>
                <SweetAlertWrapper
                    show={showAlert}
                    title="Notification"
                    message={alertMessage.text}
                    onConfirm={handleConfirm}
                    confirmBtnText="OK"
                    type={alertMessage.type || "info"}
                />

                {selectedVariance && showUpdateTotalizersModal && (
                    <UpdateTotalizersModal
                        show={showUpdateTotalizersModal}
                        handleClose={handleClose}
                        initialValues={selectedVariance}
                    />
                )}

                {/* Card with export button */}
                <div className="card">
                    <div className="card-body">
                        <h5 className="card-title">List of Shift Variances</h5>

                        <div className="d-flex justify-content-between mb-3">
                            <Button
                                className="btn btn-success m-1"
                                onClick={() =>
                                    handle_downloads(
                                        () => exportVariancesReport(),
                                        setDownloadLoading,
                                    )
                                }
                                disabled={download_loading}
                            >
                                {download_loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />{" "}
                                        Downloading Excel...
                                    </>
                                ) : (
                                    "Export Variance Report"
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="row mb-4">
                    <div className="col-md-4">
                        <Form.Control
                            type="text"
                            placeholder="Search by station, shift number or attendant"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="col-md-4">
                        <Form.Select
                            value={filterStation}
                            onChange={(e) => setFilterStation(e.target.value)}
                        >
                            <option value="">All Stations</option>
                            {[...new Set((shift_variances || []).map((v) => v.stationName))]
                                .filter(Boolean)
                                .map((station) => (
                                    <option key={station} value={station}>
                                        {station}
                                    </option>
                                ))}
                        </Form.Select>
                    </div>
                    <div className="col-md-4">
                        <Form.Select
                            value={filterNozzle}
                            onChange={(e) => setFilterNozzle(e.target.value)}
                        >
                            <option value="">All Nozzles</option>
                            {[...new Set((shift_variances || []).map((v) => v.nozzleName))]
                                .filter(Boolean)
                                .map((nozzle) => (
                                    <option key={nozzle} value={nozzle}>
                                        {nozzle}
                                    </option>
                                ))}
                        </Form.Select>
                    </div>
                </div>

                {/* Table */}
                <table className="table">
                    <thead>
                        <tr>
                            <th>Shift</th>
                            <th>Station</th>
                            <th>Dispenser</th>
                            <th>Nozzle</th>
                            <th>Attendant</th>
                            <th>Date</th>
                            <th>Opening</th>
                            <th>Closing</th>
                            <th>Expected Closing</th>
                            <th>Sold Qty</th>
                            <th>Variance</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredVariances.length === 0 ? (
                            <tr>
                                <td colSpan="12" className="text-center">
                                    {shift_variances.length === 0
                                        ? "No shift variances found"
                                        : "No results match your filters"}
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((shiftvariance) => (
                                <tr key={shiftvariance.id}>
                                    <td>{shiftvariance.shiftNumber}</td>
                                    <td>{shiftvariance.stationName}</td>
                                    <td>{shiftvariance.dispenserName}</td>
                                    <td>{shiftvariance.nozzleName}</td>
                                    <td>{shiftvariance.name}</td>
                                    <td>{formatDate(shiftvariance.dateCreated)}</td>
                                    <td>{shiftvariance.openingReading}</td>
                                    <td>{shiftvariance.closingReading}</td>
                                    <td>{shiftvariance.expectedClosingReading}</td>
                                    <td>{shiftvariance.quantitySold}</td>
                                    <td>{shiftvariance.variance}</td>
                                    <td>
                                        <div className="dropdown">
                                            <button
                                                className="btn btn-secondary dropdown-toggle"
                                                type="button"
                                                id="dropdownMenuButton"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                            >
                                                Actions
                                            </button>
                                            <ul
                                                className="dropdown-menu"
                                                aria-labelledby="dropdownMenuButton"
                                            >
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() =>
                                                            handleDefer(shiftvariance.shiftNumber)
                                                        }
                                                        disabled={
                                                            !userCan(user, "POST:Sales:DeferVariance")
                                                        }
                                                    >
                                                        Defer Variance
                                                    </button>
                                                </li>
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() =>
                                                            handleWriteOff(shiftvariance.shiftNumber)
                                                        }
                                                        disabled={
                                                            !userCan(user, "POST:Sales:WriteOffVariance")
                                                        }
                                                    >
                                                        Write Off Variance
                                                    </button>
                                                </li>
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() =>
                                                            handleShowAddSaleModal(shiftvariance)
                                                        }
                                                    >
                                                        Add Sale
                                                    </button>
                                                </li>
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            setSelectedVariance(shiftvariance); // full object
                                                            setShowUpdateTotalizersModal(true);
                                                        }}
                                                    >
                                                        Update Totalizers
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination (based on filtered data) */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                />

                {/* Defer Modal */}
                <Modal show={showDeferModal} onHide={() => setShowDeferModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Defer Variance</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>Reason for Deferring</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter reason"
                                value={deferReason}
                                onChange={(e) => setDeferReason(e.target.value)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowDeferModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleConfirmDefer}>
                            Confirm Defer
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Write Off Modal */}
                <Modal
                    show={showWriteOffModal}
                    onHide={() => setShowWriteOffModal(false)}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Write Off Variance</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            Are you sure you want to write off the variance for Shift Number{" "}
                            {selectedVariance}?
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowWriteOffModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleConfirmWriteOff}>
                            Confirm Write Off
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Add Sale Modal */}
                <AddSaleModal
                    show={showAddSaleModal}
                    handleClose={handleClose}
                    selectedVariance={selectedVariance}
                />
            </Main>
        </>
    );
};

export default Shiftvariances;
